/*
 * Zardo product card
 * 1. Pointer-tracking 3D tilt + image parallax for [data-zardo-card-tilt] media windows.
 * 2. Added / error feedback on the quick-add button.
 *
 * Everything is delegated from document so cards injected later (infinite scroll,
 * rail feeds, sealed tabs, section reloads) work without re-initialisation.
 */
(() => {
  const TILT_SELECTOR = '[data-zardo-card-tilt]';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TILT_PROPS = ['--zc-rx', '--zc-ry', '--zc-px', '--zc-py'];

  let activeFrame = null;
  let activeRect = null;
  let maxTiltX = 3;  // rotateX: top/bottom nod, kept small
  let maxTiltY = 14; // rotateY: side-to-side swing, the main motion
  let pointerX = 0;
  let pointerY = 0;
  let rafId = 0;

  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  const resetFrame = (frame) => {
    frame.classList.remove('is-tilting');
    TILT_PROPS.forEach((prop) => frame.style.removeProperty(prop));
  };

  const paint = () => {
    rafId = 0;
    if (!activeFrame || !activeRect) return;

    const nx = clamp01((pointerX - activeRect.left) / activeRect.width);
    const ny = clamp01((pointerY - activeRect.top) / activeRect.height);

    // The side under the pointer recedes; the image floats the other way.
    // Horizontal pointer travel drives most of the motion.
    const rotateX = (0.5 - ny) * 2 * maxTiltX;
    const rotateY = (nx - 0.5) * 2 * maxTiltY;

    activeFrame.style.setProperty('--zc-rx', `${rotateX.toFixed(2)}deg`);
    activeFrame.style.setProperty('--zc-ry', `${rotateY.toFixed(2)}deg`);
    activeFrame.style.setProperty('--zc-px', `${((0.5 - nx) * 8).toFixed(2)}%`);
    activeFrame.style.setProperty('--zc-py', `${((0.5 - ny) * 2).toFixed(2)}%`);
  };

  const startTilt = (frame) => {
    if (activeFrame && activeFrame !== frame) resetFrame(activeFrame);
    activeFrame = frame;
    activeRect = frame.getBoundingClientRect();
    const style = getComputedStyle(frame);
    maxTiltX = parseFloat(style.getPropertyValue('--zc-tilt-x')) || 3;
    maxTiltY = parseFloat(style.getPropertyValue('--zc-tilt-y')) || 14;
    frame.classList.add('is-tilting');
  };

  const stopTilt = () => {
    if (!activeFrame) return;
    resetFrame(activeFrame);
    activeFrame = null;
    activeRect = null;
  };

  document.addEventListener('pointerover', (event) => {
    if (!finePointer.matches || reducedMotion.matches) return;
    const frame = event.target instanceof Element ? event.target.closest(TILT_SELECTOR) : null;
    if (!frame || frame === activeFrame) return;
    startTilt(frame);
  });

  document.addEventListener('pointermove', (event) => {
    if (!activeFrame) return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    if (!rafId) rafId = requestAnimationFrame(paint);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    if (!activeFrame) return;
    const next = event.relatedTarget;
    if (next instanceof Node && activeFrame.contains(next)) return;
    if (!(event.target instanceof Node) || !activeFrame.contains(event.target)) return;
    stopTilt();
  });

  document.addEventListener('pointerdown', (event) => {
    // Keep the media flat while a quick-add is being pressed so the button stays under the cursor.
    if (activeFrame && event.target instanceof Element && event.target.closest('.zardo-card__quick')) stopTilt();
  }, true);

  window.addEventListener('scroll', () => {
    if (activeFrame) activeRect = activeFrame.getBoundingClientRect();
  }, { passive: true, capture: true });

  window.addEventListener('blur', stopTilt);

  // ---------------------------------------------------------------- quick-add feedback
  const flash = (button, className) => {
    if (!button) return;
    button.classList.remove('is-added', 'is-error');
    button.classList.add(className);
    window.clearTimeout(button._zardoFlash);
    button._zardoFlash = window.setTimeout(() => button.classList.remove(className), 1600);
  };

  const buttonFor = (event) => {
    const form = event.target instanceof Element ? event.target.closest('form.zardo-card__form') : null;
    return form ? form.querySelector('.zardo-card__add') : null;
  };

  document.addEventListener('variant:add', (event) => flash(buttonFor(event), 'is-added'));
  document.addEventListener('cart:error', (event) => flash(buttonFor(event), 'is-error'));
})();
